import React, { useState, useEffect } from "react";
import {
  resolveCardBackground,
  cardContainerStyle,
  cardOuterStyle,
  messageStyle as sharedMessageStyle,
} from "../../utils/barCardSurface";
import { hasAnimatedMessage } from "../../utils/messageAnimation";
import AnimatedMessage from "./AnimatedMessage";

export { resolveCardBackground };

export function symbolFor(code) {
  const map = { USD: "$", EUR: "\u20AC", GBP: "\u00A3", INR: "Rs. ", AUD: "$", CAD: "$", JPY: "\u00A5" };
  return map[code] || "$";
}

export function money(sym, amount) {
  return `${sym}${Number(amount || 0).toFixed(2)}`;
}

export function applyTokens(tpl, goalStr, progressStr) {
  return String(tpl || "")
    .replace(/\{order-value-progress\}/g, progressStr)
    .replace(/\{order-value\}/g, goalStr)
    .replace(/\{remaining\}/g, progressStr);
}

function safeCssColor(value, fallback) {
  const c = String(value || fallback || "#000000").trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c) || /^rgb/i.test(c) ? c : fallback;
}

/** SVG progress bar — wrapper border-radius clips fill for perfect pill ends. */
export function ProgressBarSvg({ pct, pb }) {
  const h = Math.max(6, Number(pb?.height ?? 8));
  const radius = Math.min(Number(pb?.corner_radius ?? 4), h / 2);
  let trackBg = safeCssColor(pb?.background, "#E4E5E7");
  let fillBg = safeCssColor(pb?.fill, "#000000");
  if (fillBg.toLowerCase() === trackBg.toLowerCase()) {
    fillBg = fillBg === "#FFFFFF" || fillBg === "#fff" ? "#000000" : "#FFFFFF";
  }
  const fillW = Math.min(100, Math.max(0, Number(pct) || 0));

  return (
    <div
      className="fsb-progress"
      data-fsb-fill={Math.round(fillW)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fillW)}
      style={{
        display: "block",
        position: "relative",
        zIndex: 2,
        width: "100%",
        height: h,
        minHeight: h,
        lineHeight: 0,
        overflow: "hidden",
        borderRadius: radius,
      }}
    >
      <svg
        className="fsb-progress-svg"
        width="100%"
        height={h}
        viewBox={`0 0 100 ${h}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <rect x="0" y="0" width="100" height={h} fill={trackBg} />
        {fillW > 0 ? <rect x="0" y="0" width={fillW} height={h} fill={fillBg} /> : null}
      </svg>
    </div>
  );
}

/** Products to show in upsell row (specific selection or FBT placeholder). */
export function resolveUpsellProducts(upsell) {
  const mode = upsell?.mode || "none";
  if (mode === "none") return [];
  if (mode === "specific") {
    return Array.isArray(upsell.products) ? upsell.products : [];
  }
  if (mode === "frequently_bought") {
    return [
      {
        id: "fbt-1",
        title: "Recommended product",
        price: 1000,
        compare_price: 1999,
        image: null,
      },
      {
        id: "fbt-2",
        title: "Recommended product",
        price: 1000,
        compare_price: 1999,
        image: null,
      },
    ];
  }
  return [];
}

/** Upsell row with optional left/right arrows when multiple products are selected. */
export function UpsellProductRow({ products, design, upsell, sym }) {
  const [index, setIndex] = useState(0);
  const list = products || [];
  const count = list.length;
  const safeIndex = count ? Math.min(index, count - 1) : 0;
  const p = list[safeIndex];
  const titleTypo = design?.typography?.product_title || {};
  const priceTypo = design?.typography?.product_price || {};
  const compareTypo = design?.typography?.compare_price || {};
  const btn = design?.button || {};
  const arrowColor = design?.icon?.arrow_color || "#BBC0C4";
  const ctaText = (upsell && upsell.cta && upsell.cta.button_text) || "Add to cart";
  const showArrows = count > 1;

  useEffect(() => {
    if (index >= count && count > 0) setIndex(0);
  }, [count, index]);

  if (!p) return null;

  const goPrev = () => setIndex((i) => (i <= 0 ? count - 1 : i - 1));
  const goNext = () => setIndex((i) => (i >= count - 1 ? 0 : i + 1));

  const arrowBtnStyle = {
    border: "none",
    background: "transparent",
    padding: "4px 6px",
    cursor: "pointer",
    color: arrowColor,
    fontSize: 22,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <div className="fsb-widget-upsell">
      {showArrows ? (
        <button type="button" aria-label="Previous product" style={arrowBtnStyle} onClick={goPrev}>
          &#8249;
        </button>
      ) : null}
      <div className="fsb-widget-upsell-main">
        <div
          className={`fsb-widget-upsell-thumb${p.image ? "" : " fsb-widget-upsell-thumb--placeholder"}`}
          style={p.image ? { backgroundImage: `url(${p.image})` } : undefined}
        />
        <div className="fsb-widget-upsell-text">
          <div
            className="fsb-widget-upsell-title"
            style={{
              fontSize: titleTypo.size ?? 16,
              color: titleTypo.color || "#202223",
            }}
          >
            {p.title || "Recommended product"}
          </div>
          <div className="fsb-widget-upsell-prices">
            <span
              style={{
                fontSize: priceTypo.size ?? 14,
                color: priceTypo.color || "#202223",
              }}
            >
              {money(sym, (p.price ?? 0) / 100)}
            </span>
            {p.compare_price != null && p.compare_price > (p.price ?? 0) ? (
              <span
                style={{
                  fontSize: compareTypo.size ?? 14,
                  color: compareTypo.color || "#878E99",
                  textDecoration: "line-through",
                }}
              >
                {money(sym, p.compare_price / 100)}
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="fsb-widget-upsell-btn"
          style={{
            border: `1px solid ${btn.border_color || "#000"}`,
            color: btn.text_color || "#000",
            background: "#FFFFFF",
            fontSize: btn.text_size ?? 14,
            borderRadius: btn.corner_radius ?? 4,
          }}
        >
          {ctaText}
        </button>
      </div>
      {showArrows ? (
        <button type="button" aria-label="Next product" style={arrowBtnStyle} onClick={goNext}>
          &#8250;
        </button>
      ) : null}
    </div>
  );
}

/** Single shipping bar widget (message + progress + optional upsell). */
export function BarWidget({ message, pct, design, upsell, sym, placementType = "product_page" }) {
  const card = design?.card || {};
  const pb = design?.progress_bar || {};
  const products = resolveUpsellProducts(upsell);
  const showUpsell = (upsell?.mode || "none") !== "none" && products.length > 0;
  const msgTypo = design?.typography?.message || {};
  const showMessage = msgTypo.visible !== false;
  const showProgressBar = pb.visible !== false;
  const useAnimation = hasAnimatedMessage(design);
  const hasContentBelow = showProgressBar || showUpsell;
  const msgStyle = sharedMessageStyle(design?.typography, placementType, hasContentBelow);

  return (
    <div className="fsb-widget-outer" style={cardOuterStyle(card, placementType, design?.layout)}>
      <div className="fsb-widget-card" style={cardContainerStyle(card, placementType, design?.layout)}>
        {showMessage ? (
          useAnimation ? (
            <AnimatedMessage message={message} design={design} style={msgStyle} />
          ) : (
            <p className="fsb-widget-msg" style={msgStyle}>
              {message}
            </p>
          )
        ) : null}
        {showProgressBar ? (
          <div style={{ position: "relative", zIndex: 1 }}>
            <ProgressBarSvg pct={pct} pb={pb} />
          </div>
        ) : null}
        {showUpsell ? (
          <div style={{ position: "relative", zIndex: 1 }}>
            <UpsellProductRow products={products} design={design} upsell={upsell} sym={sym} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
